/**
 * *Undo this import?* (`equipment-page-undo-import`, R12–R14; Equipment frame 11).
 *
 * Card 08's destructive `Modal` (coral), shown only once something from the file is placed. It says
 * the whole file plainly — what is removed, what goes back, what is already on the floor plan — then
 * lists each placed asset with where it is, as Floor Plan's delete confirmation does, and the capacity
 * change. *Keep the Import* is focused first and is the default; Escape and the close cross keep it.
 *
 * Presentational: `useEquipmentImportUndo` decides what it says and what *Undo Import* does.
 */
import React from 'react';
import styled from 'styled-components';
import { Banner, Button, Icon, Modal, Note, Tag } from '@repo/design-system';
import { fontMono, ink, line, radius, semantic, space, typeScale } from '@repo/design-tokens';

import { EquipmentModalBody } from './equipmentModalBody';
import type { UndoConfirmation } from './useEquipmentImportUndo';

export interface EquipmentImportUndoModalProps {
  confirmation: UndoConfirmation | null;
  busy: boolean;
  onKeep: () => void;
  onUndo: () => void;
  /** The button that opened it: focus goes back there when the import is kept. */
  returnFocusTo: React.RefObject<HTMLElement | null>;
}

const plural = (n: number, one: string, many: string): string => `${n} ${n === 1 ? one : many}`;

const Rows = styled.ul`
  && {
    list-style: none;
    padding: 0;
  }
  display: flex;
  flex-direction: column;
  gap: ${space[2]};
`;

const Row = styled.li`
  display: flex;
  align-items: center;
  gap: ${space[3]};
  padding: ${space[2]} ${space[3]};
  background: ${semantic.surface.card};
  border: 1px solid ${line[2]};
  border-radius: ${radius.md};

  .row-what {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .row-change {
    display: flex;
    align-items: center;
    gap: ${space[2]};
    flex-shrink: 0;
    font-family: ${fontMono};
    font-size: ${typeScale.caption1.fontSize};
    line-height: ${typeScale.caption1.lineHeight};
    color: ${ink[3]};
  }
  .row-from {
    max-width: 12ch;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const Foot = styled.p`
  && {
    color: ${ink[3]};
    font-size: ${typeScale.caption1.fontSize};
    line-height: ${typeScale.caption1.lineHeight};
  }
`;

/** The body sentence (R12): each clause only when its count is non-zero. */
export const undoSentence = (c: UndoConfirmation): string => {
  const clauses = [
    ...(c.added > 0 ? [`${plural(c.added, 'added asset is', 'added assets are')} removed`] : []),
    ...(c.updated > 0
      ? [`${plural(c.updated, 'updated asset goes', 'updated assets go')} back to how they were`]
      : []),
  ];
  const head = `The whole of ${c.fileName} is undone${
    clauses.length > 0 ? `: ${clauses.join(' and ')}` : ''
  }.`;
  const placed =
    c.placed > 0
      ? ` ${c.placed} of the added assets ${c.placed === 1 ? 'is' : 'are'} already on the floor plan.`
      : '';
  return head + placed;
};

/** The capacity sentence (R13), or none when it would not change or cannot be said. */
export const capacitySentence = (c: UndoConfirmation): string | null =>
  c.capacity === null
    ? null
    : `Their spaces’ calculated capacities ${c.capacity.kind} by ${c.capacity.by}.`;

export const EquipmentImportUndoModal: React.FC<EquipmentImportUndoModalProps> = ({
  confirmation,
  busy,
  onKeep,
  onUndo,
  returnFocusTo,
}) => {
  const c = confirmation;
  const capacity = c ? capacitySentence(c) : null;
  return (
    <Modal
      open={c !== null}
      onClose={onKeep}
      tone="coral"
      icon={<Icon name="rotate-left" />}
      title="Undo this import?"
      dismissible={!busy}
      returnFocusTo={returnFocusTo}
      actions={
        <>
          <Button variant="text" autoFocus disabled={busy} onClick={onKeep}>
            Keep the Import
          </Button>
          <Button
            variant="destructive"
            iconLeft={<Icon name="rotate-left" size="sm" />}
            loading={busy}
            disabled={!!c?.refusal}
            onClick={onUndo}
          >
            Undo Import
          </Button>
        </>
      }
    >
      {c && (
        <EquipmentModalBody>
          <p>{undoSentence(c)}</p>
          <Rows aria-label="What the undo changes">
            {c.lines.map(entry => (
              <Row key={entry.key}>
                <Icon name="person-running" size="sm" />
                <span className="row-what">
                  {entry.name} · {entry.where}
                </span>
                <span className="row-change">
                  On plan
                  <Icon name="arrow-right" size="xs" />
                  <Tag surface="on-solid" size="dense" tone="error">
                    Removed
                  </Tag>
                </span>
              </Row>
            ))}
            {c.updated > 0 && (
              <Row>
                <Icon name="pen" size="sm" />
                <span className="row-what">
                  {plural(c.updated, 'updated asset', 'updated assets')}
                </span>
                <span className="row-change">
                  <span className="row-from" title={c.fileName}>
                    {c.shortName}
                  </span>
                  <Icon name="arrow-right" size="xs" />
                  <Tag surface="on-solid" size="dense" tone="error">
                    Previous values
                  </Tag>
                </span>
              </Row>
            )}
          </Rows>
          {c.cascade.length > 0 && (
            <>
              <p>With the placements:</p>
              <ul aria-label="Goes with the placements">
                {c.cascade.map(entry => (
                  <li key={entry}>{entry}</li>
                ))}
              </ul>
            </>
          )}
          {c.editedSince > 0 && (
            <p>
              {c.editedSince === 1
                ? '1 asset was changed after the upload and keeps those changes.'
                : `${c.editedSince} assets were changed after the upload and keep those changes.`}
            </p>
          )}
          <Foot>{[capacity, 'This can’t be undone.'].filter(Boolean).join(' ')}</Foot>
          {c.changed && (
            <Note tone="butter" role="alert" icon={<Icon name="triangle-exclamation" size="xs" />}>
              What this takes has changed since this opened. Check the list, then Undo Import again.
            </Note>
          )}
          {c.refusal && (
            <Banner surface="on-solid" tone="error">
              {c.refusal}
            </Banner>
          )}
          {c.error && (
            <Banner surface="on-solid" tone="error" title={c.error.title}>
              {c.error.message}
            </Banner>
          )}
        </EquipmentModalBody>
      )}
    </Modal>
  );
};
